import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    computed,
    inject,
    signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ExamAssistantPanelComponent } from '../exam-assistant-panel/exam-assistant-panel.component';

/** Phases du parcours de démo. */
type DemoStage = 'intro' | 'exercise' | 'done';

/** Types d'exercices factices présentés (un choix, une écoute, un oral). */
type DemoExerciseKind = 'choice' | 'listening' | 'speaking';

/** Zones de l'interface présentées pendant le tour guidé. */
const TOUR_ZONES = ['question', 'media', 'answer', 'timer', 'actions'] as const;
type TourZone = (typeof TOUR_ZONES)[number];

/** Activités factices affichées dans la sidebar de progression (clés i18n). */
const SIDEBAR_ACTIVITIES = [
    'ai-exam-demo.sidebar.activity1',
    'ai-exam-demo.sidebar.activity2',
    'ai-exam-demo.sidebar.activity3',
    'ai-exam-demo.sidebar.activity4',
] as const;

/** Durée du chrono factice par exercice (secondes). */
const DEMO_TIMER_SECONDS = 75;
/** Nombre d'écoutes autorisées sur l'exercice d'écoute factice. */
const DEMO_MAX_PLAYS = 2;
/** Durée simulée de la lecture audio quand la synthèse vocale est indisponible (ms). */
const FALLBACK_PLAY_DURATION_MS = 7000;
/** Durée simulée de l'analyse de la réponse (ms). */
const FAKE_ANALYSIS_DURATION_MS = 1800;
/** Durée maximale d'un enregistrement de démo (secondes). */
const DEMO_MAX_RECORD_SECONDS = 20;

/** Mapping langue d'interface → locale BCP-47 pour la synthèse vocale. */
const UI_LANG_TO_LOCALE: Record<string, string> = {
    fr: 'fr-FR',
    en: 'en-US',
    es: 'es-ES',
    it: 'it-IT',
    de: 'de-DE',
    pt: 'pt-PT',
    nl: 'nl-NL',
};

/**
 * Démo interactive de l'ergonomie du test (FlashLevel / LILATE IA).
 *
 * Proposée à la fin de l'onboarding pendant que le test est préparé en
 * tâche de fond (chantier 8 — artifice d'attente n°1). Entièrement
 * statique : aucun exercice n'est généré par le moteur, rien n'est
 * enregistré ni évalué.
 *
 * La phase d'exercice reprend le shell plein écran du vrai test (sidebar
 * progression + zone centrale + panneau assistant droit avec chrono
 * circulaire et critères) afin que le candidat retrouve exactement la même
 * interface pendant l'examen.
 *
 * Déroulé :
 * 1. intro — proposition de la démo (skippable à tout moment) ;
 * 2. tour guidé — les zones de l'écran d'exercice sont surlignées une à
 *    une (question, document, réponse, chronomètre, boutons de secours) ;
 * 3. trois exercices factices interactifs : un choix (répondre/valider,
 *    analyse simulée chrono en pause), une écoute (lecteur à écoutes
 *    limitées via synthèse vocale du navigateur), un oral (enregistrement
 *    micro réel local + réécoute) ;
 * 4. récapitulatif des points clés.
 *
 * Usage :
 * <lib-ai-exam-onboarding-demo [testType]="'flashlevel'" [uiLang]="uiLang"
 *                              (demoFinished)="onDemoFinished()">
 * </lib-ai-exam-onboarding-demo>
 */
@Component({
    selector: 'lib-ai-exam-onboarding-demo',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslateModule,
        ExamAssistantPanelComponent,
    ],
    templateUrl: './AI-exam-onboarding-demo.component.html',
    styleUrls: ['./AI-exam-onboarding-demo.component.scss'],
})
export class AIExamOnboardingDemoComponent implements OnDestroy {
    /** Langue d'interface (locale de la synthèse vocale de la démo d'écoute). */
    @Input() uiLang = 'fr';
    /** Type de test : adapte la marque de la sidebar (FlashLevel vs LILATE). */
    @Input() testType: 'flashlevel' | 'lilate' = 'flashlevel';
    /** Émis quand la démo est terminée ou passée. */
    @Output() demoFinished = new EventEmitter<void>();

    private translate = inject(TranslateService);
    private sanitizer = inject(DomSanitizer);

    stage = signal<DemoStage>('intro');
    exerciseKind = signal<DemoExerciseKind>('choice');

    /** Activités factices de la sidebar de progression (la 1re est active). */
    readonly sidebarActivities = SIDEBAR_ACTIVITIES;
    /** Durée totale d'un exercice : référence du chrono circulaire du panneau. */
    readonly totalTimerSeconds = DEMO_TIMER_SECONDS;
    /** Repli du panneau assistant droit (comme dans le vrai test). */
    rightPanelCollapsed = signal(false);

    /** Clé i18n du libellé d'activité affiché dans le panneau assistant. */
    activityLabelKey = computed(() => `ai-exam-demo.${this.exerciseKind()}.activity-label`);
    /** Clé i18n des critères d'évaluation affichés dans le panneau assistant. */
    criteriaKey = computed(() => `ai-exam-demo.${this.exerciseKind()}.criteria`);

    /** Index de la zone surlignée pendant le tour guidé (-1 = tour inactif). */
    tourZoneIndex = signal(-1);
    tourZones = TOUR_ZONES;

    selectedChoice = signal<number | null>(null);
    /** Simulation de l'analyse de la réponse (le chrono est en pause). */
    analysisInProgress = signal(false);
    /** Encart de transition affiché après chaque exercice factice. */
    feedbackKey = signal<string | null>(null);

    // ── Chrono factice ──
    remainingSeconds = signal(DEMO_TIMER_SECONDS);
    timeoutReached = signal(false);
    private timerInterval: ReturnType<typeof setInterval> | null = null;

    // ── Écoute factice ──
    playsLeft = signal(DEMO_MAX_PLAYS);
    isPlaying = signal(false);
    playProgress = signal(0);
    private playProgressInterval: ReturnType<typeof setInterval> | null = null;
    private fallbackPlayTimeout: ReturnType<typeof setTimeout> | null = null;

    // ── Oral factice ──
    recordingState = signal<'idle' | 'recording' | 'recorded' | 'error'>('idle');
    recordSeconds = signal(0);
    /** URL blob locale de la prise (SafeUrl : jamais envoyée au serveur). */
    recordedAudioUrl = signal<SafeUrl | null>(null);
    private recordedObjectUrl: string | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private mediaStream: MediaStream | null = null;
    private recordInterval: ReturnType<typeof setInterval> | null = null;
    private recordedChunks: Blob[] = [];

    private analysisTimeout: ReturnType<typeof setTimeout> | null = null;

    /** Zone actuellement surlignée par le tour (null hors tour). */
    activeTourZone = computed<TourZone | null>(() => {
        const index = this.tourZoneIndex();
        return index >= 0 && index < TOUR_ZONES.length ? TOUR_ZONES[index] : null;
    });

    /** Le candidat peut interagir avec l'exercice factice. */
    canInteract = computed(() =>
        this.stage() === 'exercise'
        && this.activeTourZone() === null
        && !this.analysisInProgress()
        && this.feedbackKey() === null
        && !this.timeoutReached());

    /** Le chrono factice est visuellement en pause. */
    timerPaused = computed(() =>
        this.analysisInProgress() || this.feedbackKey() !== null || this.activeTourZone() !== null);

    ngOnDestroy(): void {
        this.clearAllTimers();
        this.stopSpeech();
        this.releaseRecorder();
    }

    // ─────────────────────────────────────────────────────────────────
    // Navigation du parcours de démo
    // ─────────────────────────────────────────────────────────────────

    /** Lance la démo : premier exercice factice avec le tour guidé. */
    startDemo(): void {
        this.stage.set('exercise');
        this.exerciseKind.set('choice');
        this.tourZoneIndex.set(0);
    }

    /** Quitte la démo (bouton passer, ou écran final). */
    skipDemo(): void {
        this.clearAllTimers();
        this.stopSpeech();
        this.releaseRecorder();
        this.demoFinished.emit();
    }

    /** Passe à la zone suivante du tour guidé ; le quitte à la dernière. */
    nextTourZone(): void {
        const next = this.tourZoneIndex() + 1;
        if (next >= TOUR_ZONES.length) {
            // Fin du tour : l'exercice devient interactif, le chrono démarre.
            this.tourZoneIndex.set(-1);
            this.startTimer();
        } else {
            this.tourZoneIndex.set(next);
        }
    }

    /** Sélection d'un choix dans les exercices QCU factices. */
    selectChoice(index: number): void {
        if (!this.canInteract()) {
            return;
        }
        this.selectedChoice.set(index);
    }

    /** Le bouton valider est actif (réponse fournie selon le type d'exercice). */
    canValidate(): boolean {
        if (!this.canInteract()) {
            return false;
        }
        if (this.exerciseKind() === 'speaking') {
            return this.recordingState() === 'recorded';
        }
        return this.selectedChoice() !== null;
    }

    /**
     * Valide la réponse factice : simule l'analyse (chrono en pause),
     * puis affiche l'encart de transition vers l'exercice suivant.
     */
    validateAnswer(): void {
        if (!this.canValidate()) {
            return;
        }
        this.pauseTimer();
        this.analysisInProgress.set(true);
        this.analysisTimeout = setTimeout(() => {
            this.analysisInProgress.set(false);
            this.feedbackKey.set(`ai-exam-demo.${this.exerciseKind()}.feedback`);
        }, FAKE_ANALYSIS_DURATION_MS);
    }

    /** Démonstration du bouton « Je ne peux pas répondre ». */
    skipExercise(): void {
        if (!this.canInteract()) {
            return;
        }
        if (this.recordingState() === 'recording') {
            this.stopRecording();
        }
        this.pauseTimer();
        this.feedbackKey.set('ai-exam-demo.skip-feedback');
    }

    /** Enchaîne sur l'exercice factice suivant (ou le récapitulatif final). */
    continueToNext(): void {
        this.feedbackKey.set(null);
        this.timeoutReached.set(false);
        this.selectedChoice.set(null);
        this.stopSpeech();
        this.releaseRecorder();
        this.recordingState.set('idle');
        this.recordSeconds.set(0);

        const kind = this.exerciseKind();
        if (kind === 'choice') {
            this.exerciseKind.set('listening');
            this.resetTimer();
            this.startTimer();
        } else if (kind === 'listening') {
            this.exerciseKind.set('speaking');
            this.resetTimer();
            this.startTimer();
        } else {
            this.pauseTimer();
            this.stage.set('done');
        }
    }

    // ─────────────────────────────────────────────────────────────────
    // Chrono factice
    // ─────────────────────────────────────────────────────────────────

    private startTimer(): void {
        this.pauseTimer();
        this.timerInterval = setInterval(() => {
            if (this.timerPaused()) {
                return;
            }
            const next = this.remainingSeconds() - 1;
            this.remainingSeconds.set(next);
            if (next <= 0) {
                // Pédagogie : pendant le vrai test la réponse partirait
                // automatiquement ; ici on l'explique sans conséquence.
                this.pauseTimer();
                if (this.recordingState() === 'recording') {
                    this.stopRecording();
                }
                this.timeoutReached.set(true);
                this.feedbackKey.set('ai-exam-demo.timeout');
            }
        }, 1000);
    }

    private pauseTimer(): void {
        if (this.timerInterval !== null) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    private resetTimer(): void {
        this.pauseTimer();
        this.remainingSeconds.set(DEMO_TIMER_SECONDS);
    }

    // ─────────────────────────────────────────────────────────────────
    // Écoute factice (synthèse vocale du navigateur, écoutes limitées)
    // ─────────────────────────────────────────────────────────────────

    /**
     * Lance la lecture du message factice via la synthèse vocale du
     * navigateur. Sans synthèse disponible, la lecture est simulée par la
     * seule barre de progression : le geste (écoutes limitées, pas de
     * texte affiché) reste démontré.
     */
    playListening(): void {
        if (!this.canInteract() || this.isPlaying() || this.playsLeft() <= 0) {
            return;
        }
        this.playsLeft.set(this.playsLeft() - 1);
        this.isPlaying.set(true);
        this.playProgress.set(0);

        const messageText = this.translate.instant('ai-exam-demo.listening.speech');
        const synthesis = window.speechSynthesis;
        if (synthesis && typeof SpeechSynthesisUtterance !== 'undefined') {
            synthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(messageText);
            utterance.lang = UI_LANG_TO_LOCALE[this.uiLang] || 'en-US';
            utterance.rate = 0.95;
            utterance.onend = () => this.finishPlayback();
            utterance.onerror = () => this.finishPlayback();
            this.animatePlayProgress(FALLBACK_PLAY_DURATION_MS);
            synthesis.speak(utterance);
        } else {
            this.animatePlayProgress(FALLBACK_PLAY_DURATION_MS);
            this.fallbackPlayTimeout = setTimeout(
                () => this.finishPlayback(), FALLBACK_PLAY_DURATION_MS);
        }
    }

    private animatePlayProgress(durationMs: number): void {
        const stepMs = 200;
        const increment = 100 / (durationMs / stepMs);
        this.clearPlayProgress();
        this.playProgressInterval = setInterval(() => {
            this.playProgress.set(Math.min(100, this.playProgress() + increment));
        }, stepMs);
    }

    private finishPlayback(): void {
        this.clearPlayProgress();
        this.playProgress.set(100);
        this.isPlaying.set(false);
    }

    private clearPlayProgress(): void {
        if (this.playProgressInterval !== null) {
            clearInterval(this.playProgressInterval);
            this.playProgressInterval = null;
        }
        if (this.fallbackPlayTimeout !== null) {
            clearTimeout(this.fallbackPlayTimeout);
            this.fallbackPlayTimeout = null;
        }
    }

    private stopSpeech(): void {
        this.clearPlayProgress();
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        this.isPlaying.set(false);
        this.playProgress.set(0);
        this.playsLeft.set(DEMO_MAX_PLAYS);
    }

    // ─────────────────────────────────────────────────────────────────
    // Oral factice (enregistrement micro local, jamais envoyé)
    // ─────────────────────────────────────────────────────────────────

    /** Démarre un enregistrement micro local (rien n'est envoyé au serveur). */
    async startRecording(): Promise<void> {
        if (!this.canInteract() || this.recordingState() === 'recording') {
            return;
        }
        try {
            this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            // Micro refusé/indisponible : on l'explique et on oriente vers
            // « Je ne peux pas répondre », comme pendant le vrai test.
            this.recordingState.set('error');
            return;
        }
        this.recordedChunks = [];
        this.mediaRecorder = new MediaRecorder(this.mediaStream);
        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.recordedChunks.push(event.data);
            }
        };
        this.mediaRecorder.onstop = () => {
            const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
            this.revokeRecordedUrl();
            this.recordedObjectUrl = URL.createObjectURL(blob);
            this.recordedAudioUrl.set(
                this.sanitizer.bypassSecurityTrustUrl(this.recordedObjectUrl));
            this.recordingState.set('recorded');
        };
        this.mediaRecorder.start();
        this.recordingState.set('recording');
        this.recordSeconds.set(0);
        this.recordInterval = setInterval(() => {
            const next = this.recordSeconds() + 1;
            this.recordSeconds.set(next);
            if (next >= DEMO_MAX_RECORD_SECONDS) {
                this.stopRecording();
            }
        }, 1000);
    }

    /** Arrête l'enregistrement en cours et prépare la réécoute locale. */
    stopRecording(): void {
        if (this.recordInterval !== null) {
            clearInterval(this.recordInterval);
            this.recordInterval = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
        }
        this.stopMediaStream();
    }

    /** Refaire une prise (l'enregistrement précédent est jeté). */
    resetRecording(): void {
        this.releaseRecorder();
        this.recordingState.set('idle');
        this.recordSeconds.set(0);
    }

    private stopMediaStream(): void {
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }
    }

    private revokeRecordedUrl(): void {
        if (this.recordedObjectUrl) {
            URL.revokeObjectURL(this.recordedObjectUrl);
            this.recordedObjectUrl = null;
        }
        this.recordedAudioUrl.set(null);
    }

    private releaseRecorder(): void {
        if (this.recordInterval !== null) {
            clearInterval(this.recordInterval);
            this.recordInterval = null;
        }
        if (this.mediaRecorder) {
            // Détacher le handler avant stop : la prise jetée ne doit pas
            // repasser l'état à « recorded ».
            this.mediaRecorder.onstop = null;
            this.mediaRecorder.ondataavailable = null;
            if (this.mediaRecorder.state !== 'inactive') {
                try {
                    this.mediaRecorder.stop();
                } catch {
                    // Recorder déjà arrêté.
                }
            }
        }
        this.mediaRecorder = null;
        this.recordedChunks = [];
        this.stopMediaStream();
        this.revokeRecordedUrl();
    }

    private clearAllTimers(): void {
        this.pauseTimer();
        this.clearPlayProgress();
        if (this.analysisTimeout !== null) {
            clearTimeout(this.analysisTimeout);
            this.analysisTimeout = null;
        }
    }

    /** Classes de surlignage d'une zone pendant le tour guidé. */
    zoneClass(zone: TourZone): Record<string, boolean> {
        const active = this.activeTourZone();
        return {
            'demo-zone': true,
            'demo-zone-highlight': active === zone,
            'demo-zone-dimmed': active !== null && active !== zone,
        };
    }
}
